import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Building2, CheckCircle2, AlertCircle } from 'lucide-react';
import { useSiteStatus } from '../../context/SiteStatusContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HotelSearchSettings = () => {
  const { refetchSiteStatus } = useSiteStatus();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [enabled, setEnabled] = useState(false);

  const axiosCfg = { withCredentials: true };

  const fetchHotelSearch = useCallback(async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${BACKEND_URL}/api/admin/settings/hotel-search`, axiosCfg);
      setEnabled(Boolean(r.data.hotel_search_enabled));
    } catch (e) {
      console.error('Failed to fetch hotel search settings', e);
      setMessage({ type: 'error', text: 'Failed to load hotel search settings.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHotelSearch();
  }, [fetchHotelSearch]);

  const handleToggle = async () => {
    const next = !enabled;
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const r = await axios.put(
        `${BACKEND_URL}/api/admin/settings/hotel-search`,
        { hotel_search_enabled: next },
        axiosCfg,
      );
      if (r.data?.success) {
        setEnabled(Boolean(r.data.hotel_search_enabled));
        refetchSiteStatus?.();
        setMessage({
          type: 'success',
          text: next
            ? 'Hotel search is now visible on the public site.'
            : 'Hotel search hidden from the public site.',
        });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      }
    } catch (e) {
      console.error('Failed to save hotel search settings', e);
      setMessage({
        type: 'error',
        text: e.response?.data?.detail || 'Failed to save hotel search settings.',
      });
      await fetchHotelSearch();
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <section
        data-testid="hotel-search-settings-card"
        className="bg-white rounded-xl border border-gray-200 p-6 mb-6 shadow-sm"
      >
        <div className="animate-pulse h-20 bg-gray-100 rounded-lg" />
      </section>
    );
  }

  return (
    <section
      data-testid="hotel-search-settings-card"
      className="bg-white rounded-xl border border-gray-200 p-6 mb-6 shadow-sm"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-[#659A9B]/15 flex items-center justify-center flex-shrink-0">
            <Building2 className="w-5 h-5 text-[#659A9B]" />
          </div>
          <div className="min-w-0">
            <h2 className="text-xl font-bold text-gray-900">Hotel Search (public site)</h2>
            <p className="text-sm text-gray-600 mt-1">
              Show or hide the Hotels tab on the homepage and block public hotel booking routes.
              Admin and agent hotel booking pages are not affected.
            </p>
            {enabled && (
              <span className="inline-flex mt-2 items-center gap-1 px-2 py-0.5 text-[11px] font-semibold bg-teal-100 text-teal-900 rounded">
                Hotel search is live on the public site
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          data-testid="hotel-search-toggle"
          onClick={handleToggle}
          disabled={saving}
          className={`relative inline-flex h-7 w-12 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#659A9B] disabled:opacity-50 ${
            enabled ? 'bg-[#659A9B]' : 'bg-gray-300'
          }`}
          aria-checked={enabled}
          role="switch"
          aria-label="Enable hotel search on public site"
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
              enabled ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      {message.text && (
        <div
          className={`mt-4 p-3 rounded-lg flex items-center gap-2 text-sm ${
            message.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
          ) : (
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
          )}
          {message.text}
        </div>
      )}
    </section>
  );
};

export default HotelSearchSettings;
